/* ============================================================================
   LIG — Collection Landing System (JS)
   - Filter bar dim/scroll behavior
   - Wishlist localStorage toggle
   - Reveal-on-scroll
   - Gift-finder card click → filter + scroll
   ============================================================================ */
(function () {
  'use strict';

  // This file is injected once per landing block (cluster/hero/the-rest/…), so
  // the same script tag can run many times on one page. Run init only once.
  if (window.__ligClLandingInit) return;
  window.__ligClLandingInit = true;

  const STORE_KEY = 'lig-cl-wishlist';

  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '[]'); }
    catch (_) { return []; }
  }
  function writeWishlist(list) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); } catch (_) {}
  }

  function syncWishlistButtons() {
    const list = readWishlist();
    document.querySelectorAll('.lig-cl-tile__wishlist').forEach(btn => {
      const id = btn.getAttribute('data-product-id');
      btn.setAttribute('data-active', list.includes(id) ? 'true' : 'false');
    });
  }

  function bindWishlist() {
    document.querySelectorAll('.lig-cl-tile__wishlist').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.getAttribute('data-product-id');
        if (!id) return;
        const list = readWishlist();
        const next = list.includes(id) ? list.filter(x => x !== id) : list.concat(id);
        writeWishlist(next);
        btn.setAttribute('data-active', next.includes(id) ? 'true' : 'false');
      });
    });
  }

  function applyFilter(themeId) {
    document.querySelectorAll('[data-cluster-theme]').forEach(el => {
      const t = el.getAttribute('data-cluster-theme');
      const match = themeId === 'all' || t === themeId;
      el.classList.toggle('lig-cl-cluster--dimmed', !match);
    });
    document.querySelectorAll('.lig-cl-pill').forEach(pill => {
      const t = pill.getAttribute('data-theme') || 'all';
      pill.classList.toggle('is-active', t === themeId);
    });
  }

  function flashTarget(el) {
    if (!el) return;
    el.classList.add('lig-cl-deeplink-flash');
    setTimeout(() => el.classList.remove('lig-cl-deeplink-flash'), 1600);
  }

  // Scroll to any section id (clean handle: "kids" / "old-salt" / "more").
  // Uses scrollIntoView so it honors the CSS scroll-margin-top (clears the sticky
  // header) and works at ANY distance — window.scrollTo was unreliable on this
  // very tall page under the theme's global `scroll-behavior: smooth`.
  // The behavior option overrides that CSS, so on-load can land instantly.
  function scrollToId(id, opts) {
    opts = opts || {};
    if (!id || id === 'all') return false;
    const target = document.getElementById(id);
    if (!target) return false;
    target.classList.add('is-in'); // reveal if the fade-up hadn't fired yet
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (opts.smooth && !reduce) {
      try { target.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
      catch (_) { target.scrollIntoView(); }
    } else {
      // Force instant: scrollIntoView({behavior:'auto'}) defers to the theme's
      // global `scroll-behavior: smooth`, which is slow/unreliable over the huge
      // distances on this page. Temporarily override, then restore.
      const html = document.documentElement;
      const prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      try { target.scrollIntoView({ block: 'start' }); }
      catch (_) { target.scrollIntoView(); }
      html.style.scrollBehavior = prev;
    }
    if (opts.updateHash && window.history && window.history.pushState) {
      window.history.pushState(null, '', '#' + id);
    }
    return true;
  }

  // Back-compat shim (gift-finder + archetype cards call this with a theme id).
  // Instant + flash: on this very tall page a smooth glide across regions takes
  // several seconds; an immediate jump with the highlight reads far better.
  function scrollToCluster(themeId) {
    if (scrollToId(themeId, { smooth: false })) flashTarget(document.getElementById(themeId));
  }

  function bindFilterBar() {
    document.querySelectorAll('.lig-cl-pill').forEach(pill => {
      pill.addEventListener('click', e => {
        const t = pill.getAttribute('data-theme') || 'all';
        // Pills are real <a href="#id"> — take over for smooth, offset scroll +
        // a shareable URL, but still degrade to native anchor nav without JS.
        if (pill.tagName.toLowerCase() === 'a') e.preventDefault();
        applyFilter(t);
        if (t !== 'all') {
          if (scrollToId(t, { smooth: false, updateHash: true })) flashTarget(document.getElementById(t));
        } else {
          if (window.history && window.history.pushState) {
            window.history.pushState(null, '', window.location.pathname + window.location.search);
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });
  }

  // Deep-link on load / hashchange: scroll to the hash section. Re-aligns a few
  // times (instant) to ride out lazy-image layout settling on this long page,
  // and bails the instant the visitor scrolls so it never fights them.
  function handleHash() {
    const id = (window.location.hash || '').replace(/^#/, '');
    if (!id || !document.getElementById(id)) return;
    let n = 0, stopped = false;
    const stop = () => { stopped = true; };
    const evs = ['wheel', 'touchmove', 'keydown'];
    evs.forEach(ev => window.addEventListener(ev, stop, { passive: true }));
    const cleanup = () => evs.forEach(ev => window.removeEventListener(ev, stop));
    (function tick() {
      if (stopped) { cleanup(); return; }
      scrollToId(id, { smooth: false });
      n++;
      if (n < 6) { setTimeout(tick, 280); }
      else { cleanup(); flashTarget(document.getElementById(id)); }
    })();
  }

  function bindGiftFinder() {
    document.querySelectorAll('[data-archetype-theme]').forEach(card => {
      card.addEventListener('click', e => {
        const t = card.getAttribute('data-archetype-theme');
        if (!t) return;
        // If user clicked a real anchor inside, allow native nav too
        const isAnchor = card.tagName.toLowerCase() === 'a';
        if (!isAnchor) e.preventDefault();
        applyFilter(t);
        scrollToCluster(t);
      });
    });
  }

  function bindReveal() {
    const els = document.querySelectorAll('.lig-cl-reveal');
    if (!('IntersectionObserver' in window) || els.length === 0) {
      els.forEach(el => el.classList.add('is-in'));
      return;
    }
    // threshold MUST be 0: a percentage threshold can never be met by a section
    // taller than the viewport (a 44-product band is ~7,700px — 12% of that is
    // more than a whole screen), which left big bands permanently invisible.
    // threshold 0 + a small bottom rootMargin fires as soon as the section's
    // top edge approaches the viewport, at ANY section height.
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    els.forEach(el => io.observe(el));
    // Belt-and-braces: if anything is still hidden a few seconds in (an exotic
    // observer failure), reveal it — an unanimated section beats a blank page.
    setTimeout(() => els.forEach(el => el.classList.add('is-in')), 4000);
  }

  function bindBundleAdd() {
    document.querySelectorAll('.lig-cl-look__add').forEach(btn => {
      btn.addEventListener('click', async () => {
        const raw = btn.getAttribute('data-bundle-variants') || '';
        const ids = raw.split(',').map(s => s.trim()).filter(Boolean);
        if (!ids.length) return;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          // Horizon contract (the family-builder lesson): pass section ids so
          // the response carries fresh drawer HTML, check res.ok, dispatch the
          // theme's own CartAddEvent, then open cart-drawer-component.
          const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map(n => n.dataset.sectionId).filter(Boolean);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: ids.map(id => ({ id: Number(id), quantity: 1 })), sections, sections_url: window.location.pathname })
          });
          const parsed = await res.json().catch(() => ({}));
          if (!res.ok || parsed.status) { btn.textContent = 'Try again'; return; }
          let cart = null;
          try { cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json(); } catch (e) { /* optional */ }
          try {
            const { CartAddEvent } = await import('@theme/events');
            document.dispatchEvent(new CartAddEvent(cart ?? parsed, 'lig-cl-look', { source: 'lig-cl-look', itemCount: cart && cart.item_count, sections: parsed.sections }));
          } catch (e) {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
          btn.textContent = 'Added to bag ✓';
          const drawer = document.querySelector('cart-drawer-component, cart-drawer, .cart-drawer, #cart-drawer');
          if (drawer && typeof drawer.open === 'function') { drawer.open(); }
        } catch (e) {
          btn.textContent = 'Try again';
        } finally {
          setTimeout(() => { btn.disabled = false; btn.textContent = original; }, 2500);
        }
      });
    });
  }

  function init() {
    syncWishlistButtons();
    bindWishlist();
    bindFilterBar();
    bindGiftFinder();
    bindBundleAdd();
    bindReveal();
    handleHash();
    window.addEventListener('hashchange', handleHash);
    // Re-run once images settle, in case the initial position shifted.
    window.addEventListener('load', () => setTimeout(handleHash, 50));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
